import React, { useEffect, useRef, useState } from 'react';
import { buildVideoConstraints, facingFromLabel } from '../utils/cameraSetup';
import { clipExtension, pickRecorderMime } from '../utils/recordClip';

function defaultDeviceName() {
  const ua = navigator.userAgent || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  return 'Phone';
}

function cameraError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Allow the camera and the microphone, then wait for the computer.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera or microphone was found on this phone.';
  }
  if (name === 'NotReadableError' || name === 'OverconstrainedError') {
    return 'That camera is not available. Choose another one on the computer.';
  }
  return 'Could not open the camera.';
}

function formatElapsed(ms) {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export default function RecordClip() {
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const appliedRef = useRef(null);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const stoppedRef = useRef(null);
  const queueRef = useRef([]);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const settingsRef = useRef({ cameraId: '', facing: 'environment', width: 1280, height: 720 });

  const [name, setName] = useState(() => localStorage.getItem('qure-record-device-name') || defaultDeviceName());
  const [paired, setPaired] = useState(null);
  const [pairCode, setPairCode] = useState('');
  const [session, setSession] = useState(0);
  const [needsPermission, setNeedsPermission] = useState(() => localStorage.getItem('qure-record-camera-ok') !== '1');
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [savedPath, setSavedPath] = useState('');
  const [clipUrl, setClipUrl] = useState('');

  const nameRef = useRef(name);
  const statusRef = useRef('idle');
  const errorRef = useRef('');
  const camerasRef = useRef([]);
  const savedPathRef = useRef('');
  const clipUrlRef = useRef('');

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  function setErrorState(message) {
    errorRef.current = message || '';
    if (mountedRef.current) setError(message || '');
    if (/allow the camera/i.test(message || '')) {
      localStorage.removeItem('qure-record-camera-ok');
      if (mountedRef.current) setNeedsPermission(true);
    }
  }

  function releaseStream() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    appliedRef.current = null;
    if (liveRef.current) liveRef.current.srcObject = null;
    wakeLockRef.current?.release?.().catch(() => {});
    wakeLockRef.current = null;
  }

  async function refreshCameras() {
    const list = await navigator.mediaDevices.enumerateDevices();
    camerasRef.current = list
      .filter((item) => item.kind === 'videoinput')
      .map((item) => ({
        deviceId: item.deviceId,
        label: item.label || 'Camera',
        facing: facingFromLabel(item.label),
      }));
  }

  async function openCamera(settings) {
    if (!window.isSecureContext) {
      setErrorState('Open this page with the HTTPS link. iPhone only allows the camera there.');
      statusRef.current = 'error';
      if (mountedRef.current) setPhase('error');
      return false;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErrorState('This browser cannot record video. Use Safari on iPhone or Chrome on Android.');
      statusRef.current = 'error';
      if (mountedRef.current) setPhase('error');
      return false;
    }

    releaseStream();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(buildVideoConstraints(settings));
    } catch (err) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(
          buildVideoConstraints({ ...settings, cameraId: '' })
        );
      } catch (fallbackErr) {
        setErrorState(cameraError(fallbackErr || err));
        statusRef.current = 'error';
        if (mountedRef.current) setPhase('error');
        return false;
      }
    }

    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return false;
    }
    if (stream.getAudioTracks().length === 0) {
      stream.getTracks().forEach((track) => track.stop());
      setErrorState('The microphone was not included. Allow the microphone.');
      statusRef.current = 'error';
      setPhase('error');
      return false;
    }

    streamRef.current = stream;
    appliedRef.current = settings;
    const preview = liveRef.current;
    if (preview) {
      preview.srcObject = stream;
      // Muted preview is required for iPhone autoplay. The recording still includes the mic.
      preview.muted = true;
      preview.playsInline = true;
      await preview.play().catch(() => {});
    }
    await refreshCameras().catch(() => {});
    localStorage.setItem('qure-record-camera-ok', '1');
    setNeedsPermission(false);
    setErrorState('');
    statusRef.current = 'live';
    setPhase('live');
    return true;
  }

  function sameSettings(settings) {
    const applied = appliedRef.current;
    if (!applied) return false;
    return applied.cameraId === settings.cameraId
      && applied.facing === settings.facing
      && applied.width === settings.width
      && applied.height === settings.height;
  }

  async function finishRecording(recorder, mimeType) {
    clearInterval(timerRef.current);
    wakeLockRef.current?.release?.().catch(() => {});
    wakeLockRef.current = null;
    const type = recorder.mimeType || mimeType || 'video/mp4';
    const blob = new Blob(chunksRef.current, { type });
    blobRef.current = blob;
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    const url = URL.createObjectURL(blob);
    clipUrlRef.current = url;
    if (mountedRef.current) setClipUrl(url);
    statusRef.current = 'saving';
    if (mountedRef.current) setPhase('saving');
    try {
      if (blob.size === 0) throw new Error('The recording was empty.');
      const response = await fetch('/api/recordings', {
        method: 'POST',
        headers: { 'Content-Type': type },
        body: blob,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The computer did not save the video.');
      savedPathRef.current = data.path || data.name || '';
      if (mountedRef.current) setSavedPath(savedPathRef.current);
      setErrorState('');
    } catch (err) {
      setErrorState(err?.message || 'The computer did not save the video.');
    }
    const live = streamRef.current?.getVideoTracks().some((track) => track.readyState === 'live');
    statusRef.current = live ? 'live' : 'idle';
    if (mountedRef.current) setPhase(statusRef.current);
    const done = stoppedRef.current;
    stoppedRef.current = null;
    done?.();
  }

  function stopRecorder() {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return Promise.resolve();
    return new Promise((resolve) => {
      stoppedRef.current = resolve;
      if (recorder.state === 'recording') recorder.stop();
      else resolve();
    });
  }

  async function startRecording(settings) {
    if (recorderRef.current?.state === 'recording') return;
    if (!streamRef.current || !sameSettings(settings)) {
      const opened = await openCamera(settings);
      if (!opened) return;
    }
    const stream = streamRef.current;
    const mime = pickRecorderMime((type) => MediaRecorder.isTypeSupported(type));
    let recorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      recorder = new MediaRecorder(stream);
    }
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => setErrorState('Recording failed. Press Start on the computer again.');
    recorder.onstop = () => { finishRecording(recorder, mime); };
    recorderRef.current = recorder;
    // shortcut: whole clip stays in memory; Safari mp4 is unplayable if start() is given a timeslice
    recorder.start();
    statusRef.current = 'recording';
    setErrorState('');
    if (mountedRef.current) {
      setPhase('recording');
      setElapsed(0);
    }
    const startedAt = Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (mountedRef.current) setElapsed(Date.now() - startedAt);
    }, 250);
    try {
      wakeLockRef.current = await navigator.wakeLock?.request('screen');
    } catch {
      wakeLockRef.current = null;
    }
  }

  async function runCommand(cmd) {
    const settings = cmd.settings || settingsRef.current;
    settingsRef.current = settings;
    if (cmd.action === 'connect' || cmd.action === 'apply') {
      if (statusRef.current === 'recording') return;
      await openCamera(settings);
    } else if (cmd.action === 'start') {
      await startRecording(settings);
    } else if (cmd.action === 'stop') {
      await stopRecorder();
    } else if (cmd.action === 'disconnect') {
      await stopRecorder();
      releaseStream();
      statusRef.current = 'idle';
      if (mountedRef.current) setPhase('idle');
    }
  }

  async function enqueue(cmd) {
    queueRef.current.push(cmd);
    if (busyRef.current) return;
    busyRef.current = true;
    while (queueRef.current.length) {
      const next = queueRef.current.shift();
      try {
        await runCommand(next);
      } catch (err) {
        setErrorState(err?.message || 'The phone could not follow the computer.');
      }
    }
    busyRef.current = false;
  }

  useEffect(() => {
    mountedRef.current = true;
    let cancel = false;
    let socket;
    let statusTimer;
    let retryTimer;
    let generation = 0;

    function sendStatus(current) {
      if (current.readyState !== 1) return;
      current.send(JSON.stringify({
        type: 'status',
        role: 'phone',
        name: nameRef.current,
        status: statusRef.current,
        error: errorRef.current,
        cameras: camerasRef.current,
        savedPath: savedPathRef.current,
        ready: localStorage.getItem('qure-record-camera-ok') === '1',
      }));
    }

    function connect() {
      if (cancel) return;
      const mine = ++generation;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const current = new WebSocket(`${protocol}//${window.location.host}/ws?role=phone`);
      socket = current;
      current.onopen = () => {
        if (cancel || mine !== generation) return;
        if (mountedRef.current) setPaired(true);
        sendStatus(current);
        clearInterval(statusTimer);
        statusTimer = setInterval(() => sendStatus(current), 2000);
      };
      current.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.type === 'command' && data.command) {
          if (data.command.settings) settingsRef.current = data.command.settings;
          enqueue(data.command);
        }
      };
      current.onclose = (event) => {
        clearInterval(statusTimer);
        if (cancel || mine !== generation) return;
        if (event.code === 4401) {
          if (mountedRef.current) setPaired(false);
          return;
        }
        retryTimer = setTimeout(connect, 1000);
      };
    }

    connect();
    return () => {
      cancel = true;
      generation += 1;
      mountedRef.current = false;
      clearInterval(statusTimer);
      clearTimeout(retryTimer);
      clearInterval(timerRef.current);
      socket?.close();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      releaseStream();
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    };
    // The socket reads refs. session changes only after a successful pairing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  async function joinComputer(event) {
    event.preventDefault();
    setError('');
    const response = await fetch('/api/pair', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: pairCode.trim(), name: nameRef.current }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(data.error || 'The computer did not accept that code.');
      return;
    }
    setPaired(true);
    setSession((value) => value + 1);
  }

  async function allowCamera() {
    const opened = await openCamera(settingsRef.current);
    if (!opened) return;
    releaseStream();
    statusRef.current = 'idle';
    if (mountedRef.current) setPhase('idle');
  }

  function saveOnPhone() {
    const blob = blobRef.current;
    if (!blob || !clipUrl) return;
    const filename = `recording-${Date.now()}.${clipExtension(blob.type)}`;
    const file = new File([blob], filename, { type: blob.type || 'video/mp4' });
    if (navigator.canShare?.({ files: [file] })) {
      navigator.share({ files: [file], title: 'Recording' }).catch((err) => {
        if (err?.name === 'AbortError') return;
        const link = document.createElement('a');
        link.href = clipUrl;
        link.download = filename;
        link.click();
      });
      return;
    }
    const link = document.createElement('a');
    link.href = clipUrl;
    link.download = filename;
    link.click();
  }

  const statusText = {
    idle: 'Waiting for the computer.',
    live: 'Camera on. Waiting for the computer to record.',
    recording: 'Recording.',
    saving: 'Saving the video on the computer...',
    error: 'The computer is still in control.',
  }[phase];

  return (
    <div
      className="min-h-screen bg-black text-white flex flex-col"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingTop: 'env(safe-area-inset-top)' }}
    >
      <header className="px-4 pt-4 pb-2">
        <h1 className="text-xl font-semibold m-0">Phone camera</h1>
        <p className="text-sm text-neutral-400 mt-1 mb-0">The computer starts and stops the recording.</p>
        <label className="block text-sm text-neutral-400 mt-3" htmlFor="device-name">Name on the computer</label>
        <input
          id="device-name"
          value={name}
          maxLength={40}
          onChange={(event) => {
            setName(event.target.value);
            localStorage.setItem('qure-record-device-name', event.target.value);
          }}
          className="mt-1 w-full rounded-xl bg-neutral-900 text-white px-3 py-3 border border-neutral-700"
        />
      </header>

      <div className="relative mx-4 mt-2 flex-1 min-h-[52vh] rounded-2xl overflow-hidden bg-neutral-900">
        <video
          ref={liveRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
        {phase === 'recording' && (
          <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/70 text-sm font-medium">
            <span className="text-red-500 mr-2">●</span>
            {formatElapsed(elapsed)}
          </div>
        )}
      </div>

      <p className="px-4 pt-3 mb-0 text-sm text-neutral-300" role="status">
        {paired === false ? 'Enter the code shown on the computer.' : statusText}
      </p>
      {savedPath && phase !== 'recording' && (
        <p className="px-4 pt-2 mb-0 text-sm text-green-400">Saved on the computer: {savedPath}</p>
      )}
      {error && (
        <p className="px-4 pt-2 mb-0 text-red-400 text-sm" role="alert">{error}</p>
      )}

      <div className="p-4 flex flex-col gap-3">
        {paired === false && (
          <form onSubmit={joinComputer} className="flex flex-col gap-3">
            <label className="text-sm text-neutral-400" htmlFor="pair-code">Pairing code</label>
            <input
              id="pair-code"
              value={pairCode}
              inputMode="numeric"
              autoComplete="one-time-code"
              onChange={(event) => setPairCode(event.target.value)}
              className="w-full rounded-xl bg-neutral-900 text-white px-3 py-3 border border-neutral-700 tracking-widest"
            />
            <button
              type="submit"
              className="rounded-full bg-white text-black text-lg font-semibold py-4 border-0"
              style={{ width: '100%' }}
            >
              Join this computer
            </button>
          </form>
        )}
        {paired === true && needsPermission && (
          <button
            type="button"
            onClick={allowCamera}
            className="rounded-full bg-white text-black text-lg font-semibold py-4 border-0"
            style={{ width: '100%' }}
          >
            Allow camera and microphone
          </button>
        )}
        {error && blobRef.current && phase !== 'recording' && phase !== 'saving' && (
          <button
            type="button"
            onClick={saveOnPhone}
            className="rounded-full bg-neutral-800 text-white text-lg font-semibold py-4 border-0"
            style={{ width: '100%' }}
          >
            Save on this phone
          </button>
        )}
      </div>
    </div>
  );
}
